/**
 * Envío MC-3 · /subir/<ticket> — página liviana para subir UN archivo que pidió
 * Claude. Sin login: el enlace es la llave (un solo uso, vence en 15 minutos).
 * Pensada para el celular: sacar la foto (o grabar) ahí mismo o elegir un archivo.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, CheckCircle2, FolderOpen, Loader2, Mic, Video } from 'lucide-react';
import '../styles/upload-link.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/upload-link`;
const GONE = 'Este enlace venció o ya se usó. Pedile uno nuevo a Claude.';

const ACCEPT = { image: 'image/*', video: 'video/*', audio: 'audio/*' };
const CAPTURE = {
  image: { label: 'Sacar foto', icon: Camera, capture: 'environment' },
  video: { label: 'Grabar video', icon: Video, capture: 'environment' },
  audio: { label: 'Grabar audio', icon: Mic, capture: 'user' },
};

export const errorMessage = (err, fallback = 'No se pudo subir. Probá de nuevo.') => {
  if (err?.response?.status === 410) return GONE;
  const d = err?.response?.data?.detail;
  return typeof d === 'string' && d ? d : fallback;
};

/** PUT directo al almacenamiento con progreso (axios no da progreso de subida en todos los celulares). */
export function putWithProgress(url, file, headers, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`PUT ${xhr.status}`)));
    xhr.onerror = () => reject(new Error('network'));
    xhr.send(file);
  });
}

export default function UploadLinkPage() {
  const { ticket } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | uploading | done | gone | error
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [placed, setPlaced] = useState(false);
  const captureRef = useRef(null);
  const pickRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/${ticket}`)
      .then((res) => { if (alive) { setInfo(res.data); setState('ready'); } })
      .catch((err) => { if (alive) { setState(err?.response?.status === 410 ? 'gone' : 'error'); setMessage(errorMessage(err, GONE)); } });
    return () => { alive = false; };
  }, [ticket]);

  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const choose = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setMessage('');
    if (info?.max_bytes && f.size > info.max_bytes) {
      setMessage(`El archivo pesa ${(f.size / 1048576).toFixed(1)} MB y el máximo es ${Math.round(info.max_bytes / 1048576)} MB.`);
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const send = async () => {
    if (!file) return;
    setState('uploading'); setProgress(0); setMessage('');
    try {
      const { data } = await axios.post(`${API}/${ticket}/url`, {
        filename: file.name || 'archivo', content_type: file.type || 'application/octet-stream', size: file.size,
      });
      await putWithProgress(data.upload_url, file, data.headers, setProgress);
      const done = await axios.post(`${API}/${ticket}/complete`, { original_name: file.name || null });
      setPlaced(!!done.data?.placed);
      setState('done');
    } catch (err) {
      const msg = errorMessage(err);
      setState(err?.response?.status === 410 ? 'gone' : 'ready');
      setMessage(msg);
    }
  };

  const kind = info?.kind;
  const cap = kind ? CAPTURE[kind] : null;
  const CapIcon = cap?.icon;

  return (
    <div className="ul-page" data-testid="upload-link-page">
      <div className="ul-card">
        <span className="ul-brand">Studio Media Labs · pedido de Claude</span>

        {state === 'loading' && <p className="ul-msg"><Loader2 size={16} className="animate-spin" /> Cargando…</p>}

        {(state === 'gone' || state === 'error') && (
          <>
            <h1>Enlace no disponible</h1>
            <p className="ul-msg is-error" data-testid="upload-link-error">{message || GONE}</p>
          </>
        )}

        {state === 'done' && (
          <>
            <p className="ul-done" data-testid="upload-link-done"><CheckCircle2 size={22} /> Recibido</p>
            <p className="ul-msg">
              {placed ? 'Ya está en tu proyecto.' : 'Ya está en tu Galería.'} Podés volver al chat con Claude.
            </p>
          </>
        )}

        {(state === 'ready' || state === 'uploading') && info && (
          <>
            <h1>Subí {info.kind_label}</h1>
            <p className="ul-dest">Va a {info.destination}.{info.note ? ` «${info.note}»` : ''}</p>

            {!file && (
              <>
                <label className="ul-big is-main" data-testid="upload-link-capture">
                  <CapIcon size={22} /> {cap.label}
                  <input ref={captureRef} type="file" accept={ACCEPT[kind]} capture={cap.capture} onChange={choose} />
                </label>
                <label className="ul-big" data-testid="upload-link-pick">
                  <FolderOpen size={22} /> Elegir archivo
                  <input ref={pickRef} type="file" accept={ACCEPT[kind]} onChange={choose} />
                </label>
              </>
            )}

            {file && (
              <>
                {kind === 'image' && <img className="ul-preview" src={preview} alt="Vista previa" />}
                {kind === 'video' && <video className="ul-preview" src={preview} controls playsInline />}
                {kind === 'audio' && <audio src={preview} controls style={{ width: '100%' }} />}
                <span className="ul-file">{file.name} · {(file.size / 1048576).toFixed(1)} MB</span>
                {state === 'uploading' ? (
                  <div className="ul-progress" aria-label={`Subiendo ${progress}%`}><span style={{ width: `${progress}%` }} /></div>
                ) : (
                  <>
                    <button type="button" className="ul-big is-main" onClick={send} data-testid="upload-link-send">Enviar</button>
                    <button type="button" className="ul-big" onClick={() => { setFile(null); setPreview(null); }}>Elegir otro</button>
                  </>
                )}
              </>
            )}

            {message && <p className="ul-msg is-error" data-testid="upload-link-error">{message}</p>}
            <p className="ul-foot">
              Este enlace sirve una sola vez y vence en 15 minutos. Sólo permite subir este archivo a tu
              cuenta: no muestra nada de ella.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
